/*
 * Copyright (c) 2026 CalAmp Corp.  All Rights Reserved
 */
import React, {memo, useCallback} from 'react';
import {makeStyles} from '@material-ui/core/styles';
import {ClampedTextWithTooltip} from '@ion/dls/src/components/assetcard/current/ClampedTextWithTooltip';
import {AssetVehicleIcon} from '@ion/dls/src/icons/current/AssetVehicleIcon';
import {LocationPinIcon} from '@ion/dls/src/icons/current/LocationPinIcon';
import {SystemInRangeIcon} from '@ion/dls/src/icons/current/SystemInRangeIcon';
import {SystemOutOfRangeIcon} from '@ion/dls/src/icons/current/SystemOutOfRangeIcon';
import {dlsColor} from '@ion/dls/src/theme/theme';
import {IconSize} from '@ion/dls/src/types/types';
import {RangeStatus} from './RangeStatusBadge';
import {TAG_ASSET_TYPE_ICONS, TagAssetType} from './TagTitleRow';

export type ToolCardStatus = 'assigned' | 'unassigned' | 'assignedToOther';

export interface ToolCardProps {
  assetType: TagAssetType;
  assetName: string;
  status: ToolCardStatus;
  range: RangeStatus;
  assetTypeLabel: string;
  location?: string;
  // the VIN or vehicle name this tool is attached to - required (and
  // rendered as a link) when status is 'assigned' or 'assignedToOther';
  // ignored when status is 'unassigned' ...
  assignedAssetName?: string;
  // pre-formatted (e.g. 'Last Report: 07:42 Today'), matching
  // AssetSummaryCard's own convention of accepting already-formatted date
  // strings rather than doing date formatting inside a presentational
  // component - shown only when range='out' ...
  lastReportLabel?: string;
  onAssignedAssetClick?: () => void;
  // the localized 'Unassigned' text ...
  unassignedLabel?: string;
  // the localized range label - 'In Range' / 'Out of Range' ...
  rangeLabel?: string;
}

const useStyles = makeStyles(() => ({
  card : {
    backgroundColor : dlsColor('white'),
    borderColor     : dlsColor('grey5'),
    borderStyle     : 'solid',
    borderWidth     : '1px 0',
    boxSizing       : 'border-box',
    display         : 'flex',
    flexDirection   : 'column',
    width           : '100%',
    gap             : '24px',
    padding         : '24px 16px'
  },
  header : {
    display        : 'flex',
    width          : '100%',
    alignItems     : 'flex-start',
    justifyContent : 'space-between',
    gap            : '16px'
  },
  headerName : {
    display    : 'flex',
    minWidth   : 0,
    alignItems : 'center',
    gap        : '8px'
  },
  name : {
    minWidth   : 0,
    fontFamily : 'Roboto',
    fontWeight : 500,
    fontSize   : '20px',
    lineHeight : '22px',
    color      : dlsColor('grey2')
  },
  // the header's range status, per the Tool Card design - its icon above its
  // label, right-aligned, with no chip box around them ...
  rangeStatus : {
    alignItems     : 'flex-end',
    display        : 'flex',
    flexDirection  : 'column',
    flexShrink     : 0,
    gap            : '4px',
    justifyContent : 'center'
  },
  rangeStatusLabel : {
    fontFamily    : 'Inter',
    fontWeight    : 600,
    fontSize      : '13px',
    lineHeight    : '16px',
    letterSpacing : '0.5px',
    color         : dlsColor('grey2'),
    whiteSpace    : 'nowrap'
  },
  body : {
    display       : 'flex',
    flexDirection : 'column',
    width         : '100%',
    gap           : '8px'
  },
  row : {
    display    : 'flex',
    width      : '100%',
    alignItems : 'center',
    gap        : '8px'
  },
  // the location's row holds its last report under it ...
  locationRow : {
    alignItems : 'flex-start'
  },
  locationTextStack : {
    display       : 'flex',
    flexDirection : 'column',
    minWidth      : 0,
    gap           : '8px'
  },
  rowText : {
    minWidth      : 0,
    fontFamily    : 'Inter',
    fontWeight    : 600,
    fontSize      : '13px',
    lineHeight    : '14px',
    letterSpacing : '0.5px',
    color         : dlsColor('grey2')
  },
  assignedLink : {
    minWidth   : 0,
    padding    : 0,
    border     : 'none',
    background : 'none',
    font       : 'inherit',
    textAlign  : 'left',
    cursor     : 'pointer'
  },
  assignedLinkText : {
    color : dlsColor('linkBlue')
  },
  unassignedText : {
    color : dlsColor('grey3')
  },
  lastReport : {
    fontFamily    : 'Inter',
    fontWeight    : 600,
    fontSize      : '10px',
    lineHeight    : '14px',
    letterSpacing : '0.5px',
    color         : dlsColor('grey2')
  }
}));

/**
 * ToolCard renders the taller Tag detail card (ION-7463) shown when
 * navigating from a `TagTitleRow`'s name link - header (asset type icon,
 * name, and range status), then location, asset-assignment, and asset-type
 * detail rows. Supports the full `status` x `range` variant matrix, even
 * though not every combination has an explicit Figma mockup.
 *
 * ```typescript
 *
 *   import {ToolCard} from
 *     '@ion/dls/src/components/assetdetails/current/ToolCard';
 *   ...
 *
 *   <ToolCard assetType='tool'
 *     assetName='Jacks Bag'
 *     status='assigned'
 *     range='in'
 *     assetTypeLabel='Tool'
 *     location='13505 Zachary Taylor Dr, Manasss, Virginia'
 *     assignedAssetName='JTHHP5BC3F5001754'
 *     onAssignedAssetClick={onAssignedAssetClick} />
 *
 *   // out of range - shows the Last Report line ...
 *   <ToolCard assetType='tool'
 *     assetName='Jacks Bag'
 *     status='assigned'
 *     range='out'
 *     assetTypeLabel='Tool'
 *     location='13505 Zachary Taylor Dr, Manasss, Virginia'
 *     assignedAssetName='JTHHP5BC3F5001754'
 *     lastReportLabel='Last Report: 07:42 Today'
 *     onAssignedAssetClick={onAssignedAssetClick} />
 *
 *   // unassigned ...
 *   <ToolCard assetType='tool'
 *     assetName='Jacks Bag'
 *     status='unassigned'
 *     range='in'
 *     assetTypeLabel='Tool'
 *     location='13505 Zachary Taylor Dr, Manasss, Virginia' />
 *
 * ```
 *
 */
export const ToolCard = ({
  assetType,
  assetName,
  status,
  range,
  assetTypeLabel,
  location,
  assignedAssetName,
  lastReportLabel,
  onAssignedAssetClick,
  unassignedLabel = 'Unassigned',
  rangeLabel
}: ToolCardProps) => {
  const isInRange = range === 'in';
  const RangeIcon = isInRange ? SystemInRangeIcon : SystemOutOfRangeIcon;
  const classes   = useStyles();
  const AssetIcon = TAG_ASSET_TYPE_ICONS[assetType];
  const isUnassigned   = status === 'unassigned';
  const showLastReport = range === 'out' && Boolean(lastReportLabel);

  const onAssignedAssetButtonClick = useCallback(() => {
    onAssignedAssetClick?.();
  }, [onAssignedAssetClick]);

  return (
    <div className={classes.card}>
      <div className={classes.header}>
        <div className={classes.headerName}>
          <AssetIcon size={IconSize.medium} color={dlsColor('grey3')} />
          <ClampedTextWithTooltip lines={1} className={classes.name}>
            {assetName}
          </ClampedTextWithTooltip>
        </div>
        <div className={classes.rangeStatus}>
          <RangeIcon size={IconSize.medium} color={dlsColor(isInRange ? 'caGreen' : 'caRed')} />
          <span className={classes.rangeStatusLabel}>
            {rangeLabel ?? (isInRange ? 'In Range' : 'Out of Range')}
          </span>
        </div>
      </div>
      <div className={classes.body}>
        {location && (
          <div className={`${classes.row} ${classes.locationRow}`}>
            <LocationPinIcon size={IconSize.small} color={dlsColor('grey2')} />
            <div className={classes.locationTextStack}>
              <span className={classes.rowText}>
                {location}
              </span>
              {showLastReport && (
                <span className={classes.lastReport}>
                  {lastReportLabel}
                </span>
              )}
            </div>
          </div>
        )}
        <div className={classes.row}>
          <AssetVehicleIcon size={IconSize.small} color={dlsColor('grey2')} />
          {isUnassigned
            ? (
              <span className={`${classes.rowText} ${classes.unassignedText}`}>
                {unassignedLabel}
              </span>
            )
            : (
              <button type='button'
                className={classes.assignedLink}
                onClick={onAssignedAssetButtonClick}>
                <span className={`${classes.rowText} ${classes.assignedLinkText}`}>
                  {assignedAssetName}
                </span>
              </button>
            )}
        </div>
        <div className={classes.row}>
          <AssetIcon size={IconSize.small} color={dlsColor('grey2')} />
          <span className={classes.rowText}>
            {assetTypeLabel}
          </span>
        </div>
      </div>
    </div>
  );
};

export default memo(ToolCard);
